import { useEffect, useState } from 'react';
import { Users, MessageSquare, DollarSign, TrendingUp, Clock, Target } from 'lucide-react';
import StatsCard from '../components/StatsCard';
import { Link } from 'react-router-dom';

interface OverviewStats {
  totalUsers: number;
  totalChats: number;
  totalSales: string;
  salesCount: number;
  activeChats: number;
  conversionRate: string;
}

export default function Estatisticas() {
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadStats();
  }, [filter]);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const params = new URLSearchParams();
      if (filter.start_date) params.append('start_date', filter.start_date);
      if (filter.end_date) params.append('end_date', filter.end_date);

      const res = await fetch(`${apiUrl}/api/stats/overview?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando estatísticas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Estatísticas Gerais</h1>
        <p className="text-gray-600">Visão geral do desempenho do sistema</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Filtrar Período</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Início</label>
            <input
              type="date"
              value={filter.start_date}
              onChange={(e) => setFilter({ ...filter, start_date: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Fim</label>
            <input
              type="date"
              value={filter.end_date}
              onChange={(e) => setFilter({ ...filter, end_date: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilter({
                start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                end_date: new Date().toISOString().split('T')[0]
              })}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            >
              Últimos 30 dias
            </button>
          </div>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        <StatsCard
          title="Total de Usuários"
          value={stats?.totalUsers || 0}
          icon={Users}
          iconColor="text-blue-500"
        />
        
        <StatsCard
          title="Chats Ativos"
          value={stats?.activeChats || 0}
          icon={MessageSquare}
          iconColor="text-green-500"
          subtitle={`${stats?.totalChats || 0} total no período`}
        />
        
        <StatsCard
          title="Total de Vendas"
          value={`R$ ${stats?.totalSales || '0.00'}`}
          icon={DollarSign}
          iconColor="text-emerald-500"
          subtitle={`${stats?.salesCount || 0} vendas`}
        />
        
        <StatsCard
          title="Taxa de Conversão"
          value={`${stats?.conversionRate || 0}%`}
          icon={Target}
          iconColor="text-purple-500"
        />
        
        <StatsCard
          title="Ticket Médio"
          value={`R$ ${stats && stats.salesCount > 0 ? (parseFloat(stats.totalSales) / stats.salesCount).toFixed(2) : '0.00'}`}
          icon={TrendingUp}
          iconColor="text-orange-500"
        />
        
        <StatsCard
          title="Chats por Venda"
          value={stats && stats.salesCount > 0 ? (stats.totalChats / stats.salesCount).toFixed(1) : '0'}
          icon={Clock}
          iconColor="text-indigo-500"
        />
      </div>

      {/* Links para Estatísticas Detalhadas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link 
          to="/estatisticas-chats"
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Estatísticas de Chats</h3>
              <p className="text-sm text-gray-600">Análise detalhada de atendimentos</p>
            </div>
            <MessageSquare className="h-12 w-12 text-green-500" />
          </div>
        </Link>

        <Link 
          to="/estatisticas-vendas"
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Estatísticas de Vendas</h3>
              <p className="text-sm text-gray-600">Performance de vendas e comissões</p>
            </div>
            <DollarSign className="h-12 w-12 text-emerald-500" />
          </div>
        </Link>

        <Link 
          to="/estatisticas-produtividade"
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Produtividade</h3>
              <p className="text-sm text-gray-600">Horas trabalhadas e atividades</p>
            </div>
            <TrendingUp className="h-12 w-12 text-purple-500" />
          </div>
        </Link>
      </div>
    </div>
  );
}
